import CustomButton from '../../buttons/CustomButton';
import { ButtonTypes, IMap, LoginResponseInterface, Order } from '../../../services/exports/Interfaces';
import useDeliveryThreshold from '../../../hooks/order/useDeliveryThreshold';
import useCheckoutThreshold from '../../../hooks/order/useCheckoutThreshold';
import { Trans, useTranslation } from 'react-i18next';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import { useMemo, useState } from 'react';
import { ERROR_CODES, PaymentMethod, SCREENS } from '../../../services/exports/Constants';
import useNavigate from '../../../hooks/navigation/useNavigate';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import useAlert from '../../../hooks/utility/useAlert';
import { PaymentIntent, PaymentIntentResult, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { ReservedHttpResponseCodes, ValidationErrorResponseData } from '../../../services/exports/Types';
import usePaymentCoordinator from '../../../hooks/payment/usePaymentCoordinator';
import useInitialData from '../../../hooks/global/useInitialData';
import { AxiosError, AxiosResponse } from 'axios';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import {
  OTP_VERIFICATION_PASSWORD_HEADER,
  OTP_VERIFICATION_PHONE_NUMBER_HEADER,
} from '../../../modals/OtpVerificationModal';
import { SectionType } from '../../checkout/sections/CheckoutInfoBag';
import { Link, createSearchParams } from 'react-router-dom';
import { RedirectStatus } from '../../../pages/OrderDetailsScreen';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import collect from 'collect.js';
import useAuth from '../../../hooks/global/useAuth';
import toast from 'react-hot-toast';
import useAnalytics from '../../../hooks/analytics/useAnalytics';
import useBasket from '../../../hooks/basket/useBasket';

interface Props {
  showTerms?: boolean;
  toggleCheckoutSection: (section: SectionType, open: boolean) => void;
  className?: string;
}

interface ChildButtonProps extends Omit<Props, 'toggleCheckoutSection'> {
  title: string;
  loading: boolean;
  disabled: boolean;
  setLoading: (value: boolean) => void;
  onClick: (confirmPayment: ConfirmPaymentFunction, paymentIntent?: PaymentIntent) => void;
  onPaymentSuccess: (order: Order) => void;
  onPaymentError: (message: string) => void;
}

enum PaymentStatus {
  pending = 'pending',
  succeeded = 'succeeded',
  failed = 'failed',
}
type ConfirmPaymentFunctionExtraParams = { paymentRequestPaymentMethodEvent?: PaymentRequestPaymentMethodEvent };
type ConfirmPaymentFunction = (
  order: Order,
  paymentIntent?: PaymentIntent,
  extra?: ConfirmPaymentFunctionExtraParams,
) => Promise<{ status: PaymentStatus; error: null | string; result: null | PaymentIntent }>;

const stripeResultCaster = (result: PaymentIntentResult) => ({
  status: result.error ? PaymentStatus.failed : PaymentStatus.succeeded,
  error: result.error,
  result: result.paymentIntent,
});

function StripeButton(props: ChildButtonProps) {
  const { title, loading, disabled, setLoading, onClick, onPaymentSuccess, onPaymentError, className } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { generatePath } = useNavigate();
  const { data } = useOrderFlow();
  const { isWithinThreshold } = useDeliveryThreshold();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();
  const { selectedPaymentMethod, findSavedPaymentMethod } = usePaymentFlow();
  const { paymentIntent, loadPaymentIntent } = usePaymentCoordinator();
  const stripe = useStripe();
  const elements = useElements();

  async function confirmPayment(order: Order, paymentIntent: PaymentIntent) {
    const returnUrl = `${window.location.origin}${generatePath(`/${SCREENS.ORDER_DETAILS}/${order.uuid}`)}`;

    const handler =
      {
        [PaymentMethod.Card]: () => confirmCardPayment(paymentIntent, returnUrl),
        [PaymentMethod.Paypal]: () => confirmPaypalPayment(paymentIntent, returnUrl),
        [PaymentMethod.ApplePay]: () => confirmWalletPayment(paymentIntent, order),
        [PaymentMethod.GooglePay]: () => confirmWalletPayment(paymentIntent, order),
      }[selectedPaymentMethod] ?? (async () => ({ success: true }));

    return await handler();
  }

  async function confirmCardPayment(paymentIntent: PaymentIntent, returnUrl: string) {
    const { client_secret } = paymentIntent;
    const savedCard = findSavedPaymentMethod(PaymentMethod.Card);

    if (savedCard) {
      return stripeResultCaster(await stripe.confirmCardPayment(client_secret));
    }

    return stripeResultCaster(
      await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
      }),
    );
  }

  async function confirmPaypalPayment(paymentIntent: PaymentIntent, returnUrl: string) {
    const { client_secret } = paymentIntent;
    const attachedPaypalAccount = findSavedPaymentMethod(PaymentMethod.Paypal);

    return stripeResultCaster(
      await stripe.confirmPayPalPayment(client_secret, {
        return_url: returnUrl,
        payment_method: attachedPaypalAccount?.external_payment_method_id,
      }),
    );
  }

  async function confirmWalletPayment(paymentIntent: PaymentIntent, order: Order) {
    const { client_secret } = paymentIntent;

    const paymentRequest = stripe
      .paymentRequest({
        country: company.country,
        currency: company.currency.toLowerCase(),
        total: {
          label: `Order at ${company.name}`,
          amount: Number((data.total * 100).toFixed(0)),
        },
      })
      .on('paymentmethod', async (e) => {
        const result = await stripe.confirmCardPayment(
          client_secret,
          { payment_method: e.paymentMethod.id },
          { handleActions: false },
        );

        if (result.error) {
          e.complete('fail');
          setLoading(false);

          return result;
        }

        e.complete('success');

        if (result.paymentIntent.status !== 'requires_action') {
          return onPaymentSuccess(order);
        }

        const { error } = await stripe.confirmCardPayment(client_secret);

        return error ? onPaymentError(error.message) : onPaymentSuccess(order);
      })
      .on('cancel', () => {
        loadPaymentIntent(true).then(() => setLoading(false));
      });

    paymentRequest.canMakePayment().then((result) => result && paymentRequest.show());

    return {
      status: PaymentStatus.pending,
      error: null,
      result: null,
    };
  }

  return (
    <CustomButton
      buttonType={!isWithinThreshold || !hasReachedCheckoutThreshold ? ButtonTypes.forth : ButtonTypes.primary}
      title={title}
      onClick={() => onClick(confirmPayment, paymentIntent)}
      loading={loading}
      disabled={disabled}
      className={className}
    />
  );
}

function DefaultButton(props: ChildButtonProps) {
  const { title, loading, disabled, onClick, className } = props;

  const { isWithinThreshold } = useDeliveryThreshold();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();

  const confirmPayment = async () => ({
    status: PaymentStatus.succeeded,
    error: null,
    result: null,
  });

  return (
    <CustomButton
      buttonType={!isWithinThreshold || !hasReachedCheckoutThreshold ? ButtonTypes.forth : ButtonTypes.primary}
      title={title}
      disabled={disabled}
      onClick={() => onClick(confirmPayment)}
      loading={loading}
      className={className}
    />
  );
}

export default function PayButton(props: Props) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });

  const { showTerms, toggleCheckoutSection, className } = props;

  const { onLogin } = useAuth();
  const { toggleInfoModal } = useAppMethods();
  const alert = useAlert();
  const { refresh } = useInitialData();
  const { trackBeginPurchase } = useAnalytics();
  const { items } = useBasket();
  const { navigate, generatePath } = useNavigate();
  const { toggleOrderMethodModal, toggleOtpVerificationModal } = useAppMethods();
  const { isStripePayment, loading: stripeLoading } = usePaymentCoordinator();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();
  const { isWithinMinThreshold, isWithinMaxThreshold, isWithinThreshold } = useDeliveryThreshold();
  const { selectedPaymentMethod } = usePaymentFlow();
  const { data, localData, createOrder, checkOrderNotDuplicate, setValidationErrors, resetValidationErrors } =
    useOrderFlow();

  const [loading, setLoading] = useState(false);

  async function checkout(
    confirmPayment: ConfirmPaymentFunction,
    paymentIntent?: PaymentIntent,
    extraHeaders?: IMap<string>,
  ): Promise<boolean> {
    setLoading(true);

    if (!(await checkOrderNotDuplicate())) {
      setLoading(false);

      return false;
    }

    trackBeginPurchase(data, items);
    resetValidationErrors();
    const { success, response, status } = await createOrder(
      paymentIntent,
      {
        [ERROR_CODES.ITEMS_OUT_OF_STOCK]: () => refresh().then(() => navigate(SCREENS.HOME_SCREEN)),
        [ERROR_CODES.CASH_PAYMENTS_NOT_SUPPORTED]: () => null,
      },
      extraHeaders,
    );

    if (success) {
      return onPaymentSuccess(response.data.data);
    }

    if (status === ReservedHttpResponseCodes.PaymentConfirmationRequired) {
      return await onPaymentConfirmationRequired(response, confirmPayment);
    }

    if (status === ReservedHttpResponseCodes.ValidationError) {
      return onValidationError(response.response.data);
    }

    if (status === 400) {
      return await onBadRequestError(response.response, (headers: IMap<string>) =>
        checkout(confirmPayment, paymentIntent, headers),
      );
    }

    if (
      status === ReservedHttpResponseCodes.ErrorResponseModal ||
      status === ReservedHttpResponseCodes.ErrorResponseAlertWindow
    ) {
      setLoading(false);

      return false;
    }

    setLoading(false);
    alert.error({
      title: t('error_messages.internal_error.title'),
      description: t('error_messages.internal_error.description'),
    });

    return false;
  }

  const onPaymentConfirmationRequired = async (
    response: AxiosError<{ order: Order; payment_intent: PaymentIntent }, any>,
    confirmPayment: ConfirmPaymentFunction,
  ): Promise<boolean> => {
    const { status: paymentStatus } = await confirmPayment(
      response.response.data.order,
      response.response.data.payment_intent,
    );

    if (paymentStatus === PaymentStatus.failed) {
      return onPaymentError();
    }

    if (paymentStatus === PaymentStatus.pending) {
      return false;
    }

    return onPaymentSuccess(response.response.data.order);
  };

  const onInputSectionError = (section: SectionType) => {
    toast.error(t('toasts.input_errors_found'));

    toggleCheckoutSection(section, true);
  };

  const onValidationError = (response: ValidationErrorResponseData): boolean => {
    setValidationErrors(response);
    setLoading(false);

    // Navigate to personal info section on validation error
    if (
      collect(response.rules)
        .only(['customer_first_name', 'customer_last_name', 'customer_email', 'customer_phone_number'])
        .isNotEmpty()
    ) {
      onInputSectionError(SectionType.customer_info);

      return false;
    }

    // Navigate to invoice info section on validation error
    if (collect(response.rules).only(['invoice_email']).isNotEmpty()) {
      onInputSectionError(SectionType.invoice_info);

      return false;
    }

    collect(response.rules)
      .only([
        'room_service_location_id',
        'delivery_quote_id',
        'delivery_street_name',
        'delivery_street_number',
        'delivery_city',
        'delivery_zip_code',
      ])
      .isNotEmpty() && toggleOrderMethodModal({ openModal: true });

    return false;
  };

  const onBadRequestError = async (
    response: AxiosResponse<{ error_code: string }>,
    retry: (headers: IMap<string>) => any,
  ): Promise<boolean> => {
    if (response.data.error_code === ERROR_CODES.ACCOUNT_SWITCH_REQUIRED) {
      await onLogin(response.data as unknown as LoginResponseInterface, false);
      setLoading(false);

      toggleInfoModal({
        openModal: true,
        title: t('modals.account_switch.title', { phoneNumber: localData.customer_phone_number }),
        description: t('modals.account_switch.description'),
        primaryButtonTitle: t('modals.account_switch.button'),
        onConfirm: () => window.location.reload(),
      });

      return true;
    }

    if (response.data.error_code !== ERROR_CODES.OTP_VERIFICATION_REQUIRED) {
      return false;
    }

    toggleOtpVerificationModal({
      openModal: true,
      phoneNumber: response.headers[OTP_VERIFICATION_PHONE_NUMBER_HEADER],
      onSubmit: (password: string) =>
        retry({
          [OTP_VERIFICATION_PHONE_NUMBER_HEADER]: response.headers[OTP_VERIFICATION_PHONE_NUMBER_HEADER],
          [OTP_VERIFICATION_PASSWORD_HEADER]: password,
        }),
    });
    setLoading(false);

    return false;
  };

  function onPaymentError(): boolean {
    alert.error({
      title: t('error_messages.payment_unsuccessful.title'),
      description: t('error_messages.payment_unsuccessful.description'),
    });

    setLoading(false);

    return false;
  }

  function onPaymentSuccess(order: Order): boolean {
    navigate({
      pathname: `${SCREENS.ORDER_DETAILS}/${order.uuid}`,
      search: createSearchParams({
        redirect_status: RedirectStatus.succeeded,
      }).toString(),
    });

    return true;
  }

  const title = useMemo(() => {
    if (!hasReachedCheckoutThreshold || !isWithinMinThreshold) {
      return t('add_items');
    }

    if (!isWithinMaxThreshold) {
      return t('remove_items');
    }

    if (data.total === 0) {
      return t('place_order');
    }

    return t('pay_now');
  }, [i18n.language, data.total, isWithinMinThreshold, isWithinMaxThreshold, hasReachedCheckoutThreshold]);

  async function onClick(confirmPayment: ConfirmPaymentFunction, paymentIntent?: PaymentIntent) {
    if (!isWithinThreshold || !hasReachedCheckoutThreshold) {
      return navigate(SCREENS.HOME_SCREEN);
    }

    return await checkout(confirmPayment, paymentIntent);
  }

  const childProps = {
    title,
    loading,
    disabled: stripeLoading || (data.total > 0 && !selectedPaymentMethod),
    setLoading,
    onClick,
    onPaymentSuccess,
    onPaymentError,
    className,
  };

  return (
    <>
      {isStripePayment ? <StripeButton {...childProps} /> : <DefaultButton {...childProps} />}
      {/*<div className="flex my-md gap-md">*/}
      {/*  <GooglePay />*/}
      {/*  <ApplePay />*/}
      {/*</div>*/}
      {showTerms && (
        <p className="lg:mt-lg mt-md text-sm text-gray-700">
          <Trans
            t={t}
            components={{
              TermsLink: <Link to={generatePath(SCREENS.TERMS_AND_CONDITIONS)} target="_blank" className="underline" />,
              PrivacyPolicyLink: (
                <Link to={generatePath(SCREENS.PRIVACY_POLICY)} target="_blank" className="underline" />
              ),
            }}
          >
            terms
          </Trans>
        </p>
      )}
    </>
  );
}
